import React, { useContext, useMemo } from 'react';
import './your-vehicle-features.scss';
import { DyfFeatureGuideContentProps } from '../../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';

import { FeatureArticleCards } from '../../dyf-landing/feature-articles/feature-article-gallery/feature-article-cards';
import {
    AuthenticationState,
    useAuthentication,
} from '../../../../hooks/use-authentication';
import { SignInReplaceComponent } from '../../dyf-landing/sign-in-replace-component/sign-in-replace-component';
import YourVehicleFeaturesCheckboxes from './your-vehicle-features-checkboxes/your-vehicle-features-checkboxes';
import { useCheckboxCategoriesAndFeatures } from '../../../../hooks/dyf/use-checkboxes/use-checkboxes';
import {
    BookmarkContext,
    withBookmarkContext,
} from '../../../../views/dyf-landing-view/context/dyf-bookmark-context';
import { useOnboardingFeatureToggles } from '../../../../hooks/dyf/use-onboarding-feature-toggles/use-onboarding-feature-toggles';
import { ActivityIndicator } from '../../../common/activity-indicator/activity-indicator';

interface YourVehicleFeaturesProps {
    featureGuideContent: DyfFeatureGuideContentProps;
    vin: string | null;
    userGuid: string;
    profile: any;
    flow: 'vin' | 'ymm';
    languageRegionCode: string;
}
const YourVehicleFeatures = (
    props: YourVehicleFeaturesProps
): React.JSX.Element => {
    const { featureGuideContent } = props;
    const { bookmarksData } = useContext(BookmarkContext);

    const { enableUnauthFeatureCategory } = useOnboardingFeatureToggles();
    const [authenticationState, login] = useAuthentication();

    const {
        allCategories,
        selectedFeatureCategories,
        visibleFeatures,
        allFeaturesCheckbox,
        myBookmarksCheckbox,
        loading: vehicleFeaturesLoading,
        disabledCategories,
    } = useCheckboxCategoriesAndFeatures({
        featureGuideContent,
    });

    const componentLoading = useMemo(
        () => vehicleFeaturesLoading && allCategories.current.length == 0,

        [vehicleFeaturesLoading, allCategories.current]
    );

    const shouldShowEmptyBookmarkOrArticles = useMemo(() => {
        return (
            authenticationState === AuthenticationState.Authenticated &&
            myBookmarksCheckbox.checked &&
            bookmarksData.count === 0
        );
    }, [myBookmarksCheckbox.checked, bookmarksData.count]);

    const shouldShowTopLevelSignIn = useMemo(() => {
        return false;
    }, [enableUnauthFeatureCategory, authenticationState]);

    return componentLoading ? (
        <div
            className="your-vehicle-features--wrapper loading"
            data-testid="your-vehicle-features--wrapper--loading"
        >
            <ActivityIndicator />
            <div className="spacer" style={{ height: '150px' }}></div>
        </div>
    ) : (
        <div
            className="your-vehicle-features--wrapper"
            data-testid="your-vehicle-features--wrapper"
        >
            <div className="your-vehicle-features-title">
                <h3> {featureGuideContent.vehicleFeaturesTitle}</h3>
            </div>
            {shouldShowTopLevelSignIn && (
                <div className="your-vehicle-features-sign-in">
                    <SignInReplaceComponent
                        bolded
                        handleLogin={login}
                        description={
                            featureGuideContent.vehicleFeaturesSignInDescription
                        }
                    />
                </div>
            )}
            <div className="your-vehicle-features-borderline"></div>

            <div
                className="your-vehicle-features-article-section"
                data-testid="your-vehicle-features"
            >
                <div className="your-vehicle-features-checkbox-container">
                    <YourVehicleFeaturesCheckboxes
                        isAuth={
                            authenticationState ===
                            AuthenticationState.Authenticated
                        }
                        onLogin={() => login()}
                        disabledCategories={disabledCategories}
                        allFeaturesCheckbox={allFeaturesCheckbox}
                        allCategories={allCategories.current}
                        myBookmarksCheckbox={myBookmarksCheckbox}
                        featureGuideContent={featureGuideContent}
                    />
                </div>
                <div
                    className="your-vehicle-features-card-container"
                    data-testid="feature-articles-card-container"
                >
                    <>
                        {shouldShowEmptyBookmarkOrArticles ? (
                            <div className="no-bookmark-message">
                                <h3>
                                    {
                                        featureGuideContent.noArticlesAreBookmarkedMessage
                                    }{' '}
                                </h3>
                            </div>
                        ) : (
                            <FeatureArticleCards
                                featureGuideContent={featureGuideContent}
                                currentlySelectedCategories={
                                    selectedFeatureCategories
                                }
                                userGuid={props.userGuid}
                                featureArticles={visibleFeatures}
                                flow={props.flow}
                                isAuthenticated={
                                    authenticationState ===
                                    AuthenticationState.Authenticated
                                }
                                languageRegionCode={props.languageRegionCode}
                            />
                        )}
                    </>
                </div>
            </div>
        </div>
    );
};

export default withBookmarkContext(YourVehicleFeatures);
